import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Seo } from '@/components/Seo';
import { Eye, EyeOff, LogIn, Mail, ArrowLeft, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { Logo, LogoDark } from '@/components/Logo';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.42, ease: [0.2, 0.8, 0.2, 1] } }
};

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState('login'); // login, magic-link, forgot-password
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Bitte E-Mail und Passwort eingeben');
      return;
    }
    setIsLoading(true);
    try {
      const userData = await login(email, password);
      toast.success(`Willkommen, ${userData.name}!`);
      if (userData.role === 'client') {
        navigate('/dashboard');
      } else {
        navigate('/admin');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Bitte E-Mail-Adresse eingeben');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/magic-link`, { email });
      setMagicLinkSent(true);
      toast.success('Login-Link wurde gesendet!');
    } catch (err) {
      toast.error('Fehler beim Senden des Links');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Bitte E-Mail-Adresse eingeben');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setResetSent(true);
      toast.success('Reset-Link wurde gesendet!');
    } catch (err) {
      // Always show success to prevent email enumeration
      setResetSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  const renderForm = () => {
    switch (view) {
      case 'magic-link':
        return (
          <motion.div 
            key="magic-link"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <button 
              onClick={() => { setView('login'); setMagicLinkSent(false); }}
              className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Zurück zum Login
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-teal-600" />
              </div>
              <h2 className="font-display text-xl font-bold text-slate-900">Magic Link Login</h2>
              <p className="text-sm text-slate-500 mt-1">Login ohne Passwort - einfach per E-Mail</p>
            </div>
            
            {magicLinkSent ? (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 text-center">
                <Mail className="w-10 h-10 text-teal-600 mx-auto mb-3" />
                <p className="font-medium text-teal-900">E-Mail wurde gesendet!</p>
                <p className="text-sm text-teal-700 mt-1">
                  Prüfe deinen Posteingang und klicke auf den Link.
                </p>
                <button 
                  onClick={() => setMagicLinkSent(false)}
                  className="text-sm text-teal-600 hover:text-teal-800 mt-3 underline"
                >
                  Erneut senden
                </button>
              </div>
            ) : (
              <form onSubmit={handleMagicLink} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">E-Mail</label>
                  <Input 
                    type="email" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    placeholder="ihre@email.de" 
                    className="h-11"
                    data-testid="magic-link-email-input" 
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={isLoading} 
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white h-11"
                  data-testid="magic-link-submit-button"
                >
                  {isLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird gesendet...</>
                  ) : (
                    <><Mail className="w-4 h-4 mr-2" /> Magic Link senden</>
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        );
        
      case 'forgot-password':
        return (
          <motion.div 
            key="forgot-password"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <button 
              onClick={() => { setView('login'); setResetSent(false); }}
              className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Zurück zum Login
            </button>
            
            <div className="text-center mb-6">
              <h2 className="font-display text-xl font-bold text-slate-900">Passwort vergessen?</h2>
              <p className="text-sm text-slate-500 mt-1">Wir senden dir einen Reset-Link</p>
            </div>
            
            {resetSent ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
                <Mail className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="font-medium text-slate-900">E-Mail wurde gesendet!</p>
                <p className="text-sm text-slate-600 mt-1">
                  Falls ein Konto existiert, erhältst du einen Link zum Zurücksetzen.
                </p>
                <button 
                  onClick={() => setResetSent(false)}
                  className="text-sm text-slate-600 hover:text-slate-800 mt-3 underline"
                >
                  Erneut senden
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">E-Mail</label>
                  <Input 
                    type="email" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    placeholder="ihre@email.de" 
                    className="h-11"
                    data-testid="forgot-password-email-input" 
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={isLoading} 
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11"
                  data-testid="forgot-password-submit-button"
                >
                  {isLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird gesendet...</>
                  ) : (
                    'Reset-Link senden'
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        );
        
      default:
        return (
          <motion.div 
            key="login"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <motion.div variants={fadeInUp}>
              <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Willkommen zurück</h1>
              <p className="text-sm text-slate-500 mb-8">Melden Sie sich an, um fortzufahren</p>
            </motion.div>

            <motion.form variants={fadeInUp} onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">E-Mail</label>
                <Input 
                  type="email" 
                  value={email} 
                  onChange={e => setEmail(e.target.value)} 
                  placeholder="ihre@email.de" 
                  className="h-11"
                  data-testid="login-email-input" 
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="text-sm font-medium text-slate-700">Passwort</label>
                  <button 
                    type="button" 
                    onClick={() => setView('forgot-password')}
                    className="text-xs text-teal-600 hover:text-teal-700"
                  >
                    Vergessen?
                  </button>
                </div>
                <div className="relative">
                  <Input 
                    type={showPassword ? 'text' : 'password'}
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    placeholder="Passwort"
                    className="h-11 pr-10"
                    data-testid="login-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={isLoading} className="w-full btn-lift bg-slate-900 text-white hover:bg-slate-800 h-11" data-testid="login-submit-button">
                {isLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Anmeldung...</>
                ) : (
                  <>Anmelden <LogIn className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </motion.form>
            
            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-[#F6F8FB] px-2 text-slate-500">oder</span>
              </div>
            </div>
            
            {/* Magic Link Button */}
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setView('magic-link')}
              className="w-full h-11 border-teal-200 text-teal-700 hover:bg-teal-50"
              data-testid="magic-link-button"
            >
              <Sparkles className="w-4 h-4 mr-2" /> 
              Mit Magic Link anmelden
            </Button>

            <motion.p variants={fadeInUp} className="text-center mt-6">
              <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">Zurück zur Startseite</Link>
            </motion.p>
          </motion.div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F8FB] flex" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <Seo
        title="Login"
        description="Melde dich bei marKIpilot an – mit Passwort oder passwortlos per Magic Link."
        path="/login"
        noindex
      />
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden items-center justify-center">
        <div className="noise-overlay absolute inset-0 opacity-[0.03]" />
        <div className="relative z-10 max-w-md px-12">
          <div className="mb-8">
            <LogoDark size="hero" />
          </div>
          <h2 className="font-display text-3xl font-bold text-white mb-4 leading-tight">
            Wir liefern messbare Nutzer für Ihre App.
          </h2>
          <p className="text-slate-400 leading-relaxed">
            Melden Sie sich an, um Ihre Kampagnen-Performance zu verfolgen, KPIs einzusehen und Ihr App-Wachstum zu steuern.
          </p>
          <div className="mt-8 pt-8 border-t border-slate-700">
            <div className="grid grid-cols-3 gap-4">
              <div><span className="font-display text-xl font-bold text-teal-400">500K+</span><p className="text-xs text-slate-500">Installs</p></div>
              <div><span className="font-display text-xl font-bold text-teal-400">40+</span><p className="text-xs text-slate-500">Partner</p></div>
              <div><span className="font-display text-xl font-bold text-teal-400">4</span><p className="text-xs text-slate-500">Branchen</p></div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.08 } } }} className="w-full max-w-md">
          <motion.div variants={fadeInUp} className="lg:hidden mb-8">
            <Logo size="large" />
          </motion.div>
          
          <AnimatePresence mode="wait">
            {renderForm()}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
